import React from 'react'
import { Link, useLocation, useParams } from 'react-router-dom'
import { api, resolveApiAssetUrl } from '../lib/api'
import {
  filterMonthlyBriefImagesByKind,
  sortMonthlyBriefImagesBySlideOrder,
  findSlideMetaForMonthlyBriefImage,
  parseStorageSlideIdFromMonthlyBriefImageName,
} from '../lib/monthlyBriefCardNews'
import {
  CARD_NEWS_SOURCE_MONTHLY_BRIEF,
  CARD_NEWS_SOURCE_TEMPLATE,
  mergeMonthlyIssueContent,
  parseMonthlyIssuePeriodLabel,
} from '../lib/monthlyIssueLanding'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import LeadPrivacyConsent from '../components/categoryLanding/LeadPrivacyConsent'
import { splitTags } from '../features/publicPage/registry'
import { normalizePersonalLandingState, normalizePersonalLandingSections } from '../features/personalLanding/normalize'
import {
  PersonalLandingOrgHero,
  renderPersonalLandingBodySection,
} from '../features/personalLanding/PublicLandingRender'
import {
  buildPersonalLandingNavItems,
  PersonalLandingOrgFooter,
  PersonalLandingOrgNav,
  PersonalLandingOrgStyles,
  usePersonalLandingScrollNav,
} from '../features/personalLanding/personalLandingOrgChrome'

function dedupeOrgParts(parts) {
  const out = []
  const seen = new Set()
  for (const p of parts || []) {
    const v = p == null ? '' : String(p).trim()
    if (!v) continue
    if (seen.has(v)) continue
    seen.add(v)
    out.push(v)
  }
  return out
}

export default function PublicLandingPage() {
  const { code } = useParams()
  const location = useLocation()
  const isPreview = React.useMemo(() => {
    try {
      const sp = new URLSearchParams(location?.search || '')
      return sp.get('preview') === '1' || sp.get('mode') === 'preview'
    } catch {
      return false
    }
  }, [location?.search])
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState(null)
  const [privateOwnerView, setPrivateOwnerView] = React.useState(false)
  const [leadName, setLeadName] = React.useState('')
  const [leadPhone, setLeadPhone] = React.useState('')
  const [leadTime, setLeadTime] = React.useState('')
  const [leadMsg, setLeadMsg] = React.useState('')
  const [leadPrivacyAccepted, setLeadPrivacyAccepted] = React.useState(false)
  const [consentInfo, setConsentInfo] = React.useState(null) // { retentionDays, collectUse:{version,text}, thirdParty:{version,text} }
  const [submitting, setSubmitting] = React.useState(false)
  const [submitOk, setSubmitOk] = React.useState(false)
  const [submitErr, setSubmitErr] = React.useState('')
  const [leadOpen, setLeadOpen] = React.useState(false)
  // honeypot: 사용자는 보지 못하는 필드(봇만 채우는 경우가 많음)
  const [website, setWebsite] = React.useState('')
  const [monthlyIssue, setMonthlyIssue] = React.useState({
    loading: false,
    brief: null,
    images: [],
    /** monthly_brief: API 브리프 | template: 섹션에 저장된 스냅샷 */
    source: CARD_NEWS_SOURCE_MONTHLY_BRIEF,
    periodLabel: null,
  })
  /** 생보/손보 카드뉴스 팝업 (이미지 + 슬라이드 설명) */
  const [issueModal, setIssueModal] = React.useState({ open: false, kind: 'life', idx: 0, list: [] })

  const openPublicIssueModal = React.useCallback((kind, startIdx = 0) => {
    const filtered = sortMonthlyBriefImagesBySlideOrder(
      filterMonthlyBriefImagesByKind(monthlyIssue.images, kind),
      monthlyIssue.brief?.payload,
      kind
    )
    if (!filtered.length) return
    const i = Math.max(0, Math.min(Number(startIdx) || 0, filtered.length - 1))
    setIssueModal({ open: true, kind, idx: i, list: filtered })
  }, [monthlyIssue.images, monthlyIssue.brief])

  const closePublicIssueModal = React.useCallback(() => {
    setIssueModal({ open: false, kind: 'life', idx: 0, list: [] })
  }, [])

  const { isScrolled, mobileMenuOpen, setMobileMenuOpen } = usePersonalLandingScrollNav()
  const navItems = React.useMemo(() => {
    if (!data) return []
    try {
      const { sections } = normalizePersonalLandingState(data?.landingSections, data, data?.landingPageStyle)
      return buildPersonalLandingNavItems(sections)
    } catch {
      return []
    }
  }, [data])
  const scrollToTop = React.useCallback(() => {
    try {
      window.scrollTo({ top: 0, behavior: 'smooth' })
    } catch {
      window.scrollTo(0, 0)
    }
  }, [])
  const scrollToAnchor = React.useCallback((anchorId) => {
    if (!anchorId) return
    const el = document.getElementById(anchorId)
    if (el?.scrollIntoView) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }
    setMobileMenuOpen(false)
  }, [setMobileMenuOpen])

  React.useEffect(() => {
    let ignore = false
    async function load() {
      setLoading(true)
      setError(null)
      setPrivateOwnerView(false)
      try {
        const res = await api.get(`/public/landing/${encodeURIComponent(code || '')}`, { __silent: true })
        const payload = (res?.data && res.data.data !== undefined) ? res.data.data : res.data
        if (!ignore) setData(payload || null)

        // 동의문/버전은 서버 정본을 사용한다.
        try {
          const cRes = await api.get(`/public/leads/${encodeURIComponent(code || '')}/consents`, { __silent: true })
          const cPayload = (cRes?.data && cRes.data.data !== undefined) ? cRes.data.data : cRes.data
          if (!ignore) setConsentInfo(cPayload || null)
        } catch {
          if (!ignore) setConsentInfo(null)
        }
      } catch (e) {
        const status = e?.response?.status
        if (status === 404) {
          // landingEnabled=false 인 경우에도 404를 주기 때문에,
          // "로그인한 소유자"라면 비공개 안내 화면을 보여준다.
          try {
            const meRes = await api.get('/users/me/public-page', { __silent: true })
            const mePayload = (meRes?.data && meRes.data.data !== undefined) ? meRes.data.data : meRes.data
            const myCode = mePayload?.code ? String(mePayload.code) : ''
            const disabled = mePayload?.landingEnabled === false
            if (myCode && String(myCode) === String(code) && disabled) {
              if (!ignore) {
                setData(null)
                setError(null)
                setPrivateOwnerView(true)
              }
              return
            }
          } catch { }
          if (!ignore) setError('페이지를 찾을 수 없습니다.')
        } else {
          const msg = e?.response?.data?.message || e?.message || '불러오기 실패'
          if (!ignore) setError(msg)
        }
      } finally {
        if (!ignore) setLoading(false)
      }
    }
    load()
    return () => { ignore = true }
  }, [code])

  React.useEffect(() => {
    let ignore = false
    async function loadLatestMonthlyIssue() {
      const empty = () => ({
        loading: false,
        brief: null,
        images: [],
        source: CARD_NEWS_SOURCE_MONTHLY_BRIEF,
        periodLabel: null,
      })
      if (!data?.landingSections || !Array.isArray(data.landingSections)) {
        if (!ignore) setMonthlyIssue(empty())
        return
      }
      const sections = normalizePersonalLandingSections(data.landingSections, data, data?.landingPageStyle)
      const miSection = sections.find((section) => section?.type === 'monthly-issue' && section?.enabled !== false)
      if (!miSection) {
        if (!ignore) setMonthlyIssue(empty())
        return
      }
      const cfg = mergeMonthlyIssueContent(miSection.content)

      if (cfg.cardNewsSource === CARD_NEWS_SOURCE_TEMPLATE && cfg.cardNewsTemplateEmbed?.images?.length) {
        const embed = cfg.cardNewsTemplateEmbed
        const images = embed.images.filter((it) => it?.fileId)
        const { year: py, month: pm } = parseMonthlyIssuePeriodLabel(embed.periodLabel)
        if (!ignore) {
          setMonthlyIssue({
            loading: false,
            brief: {
              year: py || null,
              month: pm || null,
              payload: embed.payload || null,
            },
            images,
            source: CARD_NEWS_SOURCE_TEMPLATE,
            periodLabel: embed.periodLabel || null,
          })
        }
        return
      }

      if (!ignore) setMonthlyIssue((prev) => ({ ...prev, loading: true }))
      try {
        const checkpoints = []
        const now = new Date()
        for (let i = 0; i < 15; i += 1) {
          const d = new Date(now.getFullYear(), now.getMonth() - i, 1)
          checkpoints.push({ year: d.getFullYear(), month: d.getMonth() + 1 })
        }

        let foundBrief = null
        let foundImages = []
        let briefWithoutImages = null
        for (const point of checkpoints) {
          const briefRes = await api.get('/insurer-newsletter-monthly-briefs/public', {
            params: { year: point.year, month: point.month },
            __silent: true,
          })
          const briefPayload = (briefRes?.data && briefRes.data.data !== undefined) ? briefRes.data.data : briefRes.data
          if (!briefPayload?.id) continue

          const imgRes = await api.get(`/insurer-newsletter-monthly-briefs/${briefPayload.id}/card-images/public`, { __silent: true })
          const imgPayload = (imgRes?.data && imgRes.data.data !== undefined) ? imgRes.data.data : imgRes.data
          const images = Array.isArray(imgPayload) ? imgPayload.filter((item) => item?.fileId) : []
          if (images.length > 0) {
            foundBrief = briefPayload
            foundImages = images
            break
          }
          if (!briefWithoutImages) {
            briefWithoutImages = briefPayload
          }
        }

        if (!foundBrief && briefWithoutImages) {
          foundBrief = briefWithoutImages
          foundImages = []
        }

        if (!ignore) {
          setMonthlyIssue({
            loading: false,
            brief: foundBrief,
            images: foundImages,
            source: CARD_NEWS_SOURCE_MONTHLY_BRIEF,
            periodLabel:
              foundBrief?.year && foundBrief?.month
                ? `${foundBrief.year}.${String(foundBrief.month).padStart(2, '0')}`
                : null,
          })
        }
      } catch {
        if (!ignore) setMonthlyIssue(empty())
      }
    }
    loadLatestMonthlyIssue()
    return () => { ignore = true }
  }, [data])

  if (loading) return <Loading />
  if (privateOwnerView) {
    return (
      <div className="min-h-screen bg-white text-slate-900 flex flex-col">
        <main className="flex-1">
          <div className="mx-auto max-w-3xl px-4 py-10">
            <div className="rounded-2xl border bg-gradient-to-b from-slate-50 to-white p-8">
              <div className="text-lg font-extrabold">비공개 처리된 소개페이지입니다.</div>
              <div className="mt-2 text-sm text-slate-600">
                현재 소개페이지 공개 설정이 꺼져 있어 외부에서는 열리지 않습니다.
              </div>
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                <Link className="rounded bg-brand-navy text-white px-4 py-2 text-sm font-semibold hover:opacity-90" to="/sales/public-landing">
                  소개페이지 관리로 이동
                </Link>
                <Link className="rounded border px-4 py-2 text-sm hover:bg-slate-50" to="/">
                  홈으로
                </Link>
              </div>
            </div>
          </div>
        </main>
      </div>
    )
  }
  if (error) return <InlineError message={error} />
  if (!data) return <InlineError message="페이지를 찾을 수 없습니다." />

  const name = data?.name || ''
  const org = dedupeOrgParts([data?.orgRootName, data?.orgParentName, data?.orgName]).join(' ')
  const phone = data?.phoneNumber || ''
  const email = data?.email || ''
  const mobileFaxNumber = data?.mobileFaxNumber || ''
  const kakaoChatUrl = data?.kakaoChatUrl || ''
  const activity = data?.activityAreaText || ''
  const career = data?.careerText || ''
  const freeText = data?.freeText || ''
  const profileImageUrl = data?.profileImageUrl || ''
  const landingGalleryEnabled = Boolean(data?.landingGalleryEnabled)
  const landingGalleryUrls = Array.isArray(data?.landingGalleryUrls) ? data.landingGalleryUrls.filter(Boolean) : []
  const showCareerCard = data?.showCareerCard !== false
  const showActivityAreaCard = data?.showActivityAreaCard !== false
  const showFreeTextCard = data?.showFreeTextCard !== false
  const imageCardEnabled = Boolean(data?.imageCardEnabled)
  const imageCardUrl = data?.imageCardUrl || ''
  const youtubeUrl = data?.youtubeUrl || ''
  const instagramUrl = data?.instagramUrl || ''
  const blogUrl = data?.blogUrl || ''
  const tags = splitTags(data?.serviceTags)
  const landingState = normalizePersonalLandingState(data?.landingSections, data, data?.landingPageStyle)
  const landingSections = landingState.sections
  const landingPageStyle = landingState.style || {}
  const heroSection = landingSections.find((s) => s.type === 'hero') || null
  const landingSkin = data?.landingTemplateId || 'LANDING_V1'
  const cardUrlPath = `/@${data?.code}`

  const fallbackConsentText = '개인정보 수집·이용에 동의합니다. (수집 항목: 이름, 연락처, (선택)희망연락시간, (선택)문의내용 / 이용 목적: 상담 신청 접수 및 상담 진행 / 보유·이용 기간: 접수일로부터 30일)'
  const fallbackConsentVersion = 'LEAD_COLLECT_USE_V1'
  const receiver = [name, org].filter(Boolean).join(' / ')
  const fallbackThirdConsentText = `개인정보 제3자 제공에 동의합니다. (제공받는 자: ${receiver || '해당 설계사'} / 제공 항목: 이름, 연락처, (선택)희망연락시간, (선택)문의내용 / 제공 목적: 상담 진행 / 보유·이용 기간: 접수일로부터 30일)`
  const fallbackThirdConsentVersion = 'LEAD_THIRD_PARTY_V1'

  const consentFormat = consentInfo?.collectUse?.format || 'TEXT'
  const consentText = consentInfo?.collectUse?.text || fallbackConsentText
  const consentVersion = consentInfo?.collectUse?.version || fallbackConsentVersion
  const thirdConsentFormat = consentInfo?.thirdParty?.format || 'TEXT'
  const thirdConsentText = consentInfo?.thirdParty?.text || fallbackThirdConsentText
  const thirdConsentVersion = consentInfo?.thirdParty?.version || fallbackThirdConsentVersion

  const contactHref = kakaoChatUrl || (phone ? `tel:${phone.replace(/[^0-9+]/g, '')}` : null)
  const phoneDigits = phone ? phone.replace(/[^0-9+]/g, '') : ''
  const smsHref = phoneDigits ? `sms:${phoneDigits}` : ''

  const landingAccentStyle = landingPageStyle?.primaryColor
    ? { '--landing-accent': String(landingPageStyle.primaryColor) }
    : undefined

  const primaryColor = landingPageStyle?.primaryColor || '#0f172a'

  const bodySectionCtx = {
    data,
    tags,
    monthlyIssue,
    openPublicIssueModal,
    landingGalleryEnabled,
    landingGalleryUrls,
    imageCardEnabled,
    imageCardUrl,
    showCareerCard,
    career,
    showActivityAreaCard,
    activity,
    showFreeTextCard,
    freeText,
    isPreview,
    setLeadOpen,
    contactHref,
    phone,
    email,
    mobileFaxNumber,
    kakaoChatUrl,
    cardUrlPath,
    layoutVariant: 'org',
    primaryColor,
  }

  const submitLead = async (e) => {
    e.preventDefault()
    setSubmitErr('')
    if (isPreview) { setSubmitErr('미리보기에서는 상담 신청 제출이 불가합니다. 실제 공유 링크에서만 제출됩니다.'); return }
    if (!leadName.trim()) { setSubmitErr('이름을 입력해 주세요.'); return }
    if (!leadPhone.trim()) { setSubmitErr('연락처를 입력해 주세요.'); return }
    if (!leadPrivacyAccepted) { setSubmitErr('개인정보 동의가 필요합니다.'); return }
    setSubmitting(true)
    try {
      await api.post(`/public/leads/${encodeURIComponent(data?.code || code || '')}`, {
        customerName: leadName,
        customerPhone: leadPhone,
        preferredContactTime: leadTime,
        message: leadMsg,
        consentAccepted: true,
        consentVersion,
        thirdPartyConsentAccepted: true,
        thirdPartyConsentVersion: thirdConsentVersion,
        website, // honeypot
      }, { __silent: true })
      setSubmitOk(true)
      setLeadOpen(false)
    } catch (err) {
      const status = err?.response?.status
      const msg = status === 429
        ? '요청이 많아 잠시 후 다시 시도해 주세요.'
        : (err?.response?.data?.message || err?.message || '접수에 실패했습니다.')

      // 동의문 버전이 변경된 경우(409): 최신 동의문을 다시 받아 재시도 유도
      if (status === 409) {
        try {
          const cRes = await api.get(`/public/leads/${encodeURIComponent(code || '')}/consents`, { __silent: true })
          const cPayload = (cRes?.data && cRes.data.data !== undefined) ? cRes.data.data : cRes.data
          setConsentInfo(cPayload || null)
        } catch { }
      }
      setSubmitErr(msg)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div
      className={`min-h-screen text-slate-900 flex flex-col ${landingSkin === 'LANDING_V2' ? 'bg-slate-50' : 'bg-white'}`}
      data-landing-skin={landingSkin}
      style={landingAccentStyle}
    >
      <PersonalLandingOrgStyles />
      <PersonalLandingOrgNav
        displayName={name || '소개'}
        navItems={navItems}
        isScrolled={isScrolled}
        mobileMenuOpen={mobileMenuOpen}
        setMobileMenuOpen={setMobileMenuOpen}
        scrollToTop={scrollToTop}
        scrollToAnchor={scrollToAnchor}
      />
      <main className="flex-1">
        <PersonalLandingOrgHero
          heroSection={heroSection}
          data={data}
          profileImageUrl={profileImageUrl}
          landingGalleryEnabled={landingGalleryEnabled}
          landingGalleryUrls={landingGalleryUrls}
          youtubeUrl={youtubeUrl}
          instagramUrl={instagramUrl}
          blogUrl={blogUrl}
          phoneDigits={phoneDigits}
          smsHref={smsHref}
          primaryColor={primaryColor}
          isPreview={isPreview}
          onPrimaryCta={() => setLeadOpen(true)}
        />

        {/* 상담 문의 모달 */}
          {issueModal.open && issueModal.list.length > 0 && (
            <div className="fixed inset-0 z-[65] flex items-center justify-center p-4">
              <button
                type="button"
                className="absolute inset-0 bg-black/60"
                onClick={closePublicIssueModal}
                aria-label="닫기"
              />
              <div className="relative flex max-h-[92vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-2xl">
                <div className="flex items-center justify-between gap-2 border-b px-4 py-3">
                  <div className="text-sm font-bold text-slate-900">
                    카드뉴스 ·{' '}
                    {monthlyIssue.periodLabel
                      || (monthlyIssue.brief?.year && monthlyIssue.brief?.month
                        ? `${monthlyIssue.brief.year}.${String(monthlyIssue.brief.month).padStart(2, '0')}`
                        : '—')}
                    {' · '}
                    {issueModal.kind === 'life' ? '생명보험' : '손해보험'}
                  </div>
                  <button
                    type="button"
                    className="text-slate-400 hover:text-slate-600 text-xl leading-none"
                    onClick={closePublicIssueModal}
                    aria-label="닫기"
                  >
                    ✕
                  </button>
                </div>
                <div className="min-h-0 flex-1 overflow-y-auto bg-slate-50 p-4">
                  {(() => {
                    const cur = issueModal.list[issueModal.idx]
                    const storageSid = parseStorageSlideIdFromMonthlyBriefImageName(cur?.imageName)
                    const slideMeta =
                      monthlyIssue.brief?.payload && storageSid
                        ? findSlideMetaForMonthlyBriefImage(monthlyIssue.brief.payload, issueModal.kind, storageSid)
                        : null
                    return (
                      <>
                        {cur?.fileId ? (
                          <img
                            src={resolveApiAssetUrl(cur.fileId)}
                            alt={slideMeta?.title || `카드뉴스 ${issueModal.idx + 1}`}
                            className="w-full max-h-[52vh] rounded-lg border border-slate-200 bg-white object-contain object-center shadow-sm"
                            loading="eager"
                            decoding="async"
                          />
                        ) : null}
                        {slideMeta && (slideMeta.title || slideMeta.body) ? (
                          <div className="mt-4 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm">
                            {slideMeta.title ? (
                              <div className="text-base font-bold text-slate-900 mb-2">{slideMeta.title}</div>
                            ) : null}
                            {slideMeta.body ? (
                              <div className="text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">{slideMeta.body}</div>
                            ) : null}
                          </div>
                        ) : null}
                      </>
                    )
                  })()}
                </div>
                <div className="flex items-center justify-between gap-2 border-t bg-white px-4 py-3">
                  <button
                    type="button"
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    onClick={() => setIssueModal((m) => ({ ...m, idx: Math.max(0, m.idx - 1) }))}
                    disabled={issueModal.idx <= 0}
                  >
                    이전
                  </button>
                  <span className="text-sm text-slate-600">
                    {issueModal.idx + 1} / {issueModal.list.length}
                  </span>
                  <button
                    type="button"
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                    onClick={() =>
                      setIssueModal((m) => ({
                        ...m,
                        idx: Math.min(m.list.length - 1, m.idx + 1),
                      }))
                    }
                    disabled={issueModal.idx >= issueModal.list.length - 1}
                  >
                    다음
                  </button>
                </div>
              </div>
            </div>
          )}

          {leadOpen && (
            <div className="fixed inset-0 z-[60]">
              <button type="button" className="absolute inset-0 bg-black/40" onClick={() => setLeadOpen(false)} aria-label="닫기" />
              <div className="relative mx-auto max-w-xl px-4 py-10">
                <div className="rounded-2xl bg-white shadow-xl border overflow-hidden">
                  <div className="flex items-center justify-between gap-2 px-5 py-4 border-b">
                    <div className="font-bold">상담 문의하기</div>
                    <button type="button" className="rounded border px-3 py-2 text-sm hover:bg-slate-50" onClick={() => setLeadOpen(false)}>닫기</button>
                  </div>
                  <div className="p-5">
                    {submitOk ? (
                      <div className="rounded-lg border bg-green-50 border-green-200 px-4 py-3 text-sm text-green-800">
                        접수되었습니다. 감사합니다.
                      </div>
                    ) : (
                      <form className="space-y-3" onSubmit={submitLead}>
                        {/* honeypot: 화면에 안 보이게 */}
                        <input
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          value={website}
                          onChange={(e) => setWebsite(e.target.value)}
                          className="hidden"
                          aria-hidden="true"
                        />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div>
                            <label className="block text-xs text-slate-600">이름 *</label>
                            <input className="mt-1 w-full rounded border px-3 py-2" value={leadName} onChange={(e) => setLeadName(e.target.value)} maxLength={80} />
                          </div>
                          <div>
                            <label className="block text-xs text-slate-600">연락처 *</label>
                            <input className="mt-1 w-full rounded border px-3 py-2" value={leadPhone} onChange={(e) => setLeadPhone(e.target.value)} maxLength={30} placeholder="01012345678" />
                          </div>
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600">희망 연락 시간(선택)</label>
                          <input className="mt-1 w-full rounded border px-3 py-2" value={leadTime} onChange={(e) => setLeadTime(e.target.value)} maxLength={80} placeholder="예) 평일 10~12시" />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-600">문의 내용(선택)</label>
                          <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[90px]" value={leadMsg} onChange={(e) => setLeadMsg(e.target.value)} maxLength={500} />
                        </div>
                        <LeadPrivacyConsent
                          collectUse={{ format: consentFormat, text: consentText }}
                          thirdParty={
                            consentInfo?.thirdParty?.text
                              ? consentInfo.thirdParty
                              : { format: thirdConsentFormat, text: thirdConsentText }
                          }
                          checked={leadPrivacyAccepted}
                          onChange={setLeadPrivacyAccepted}
                          accentColor={primaryColor}
                        />
                        {submitErr && <div className="text-xs text-red-600">{submitErr}</div>}
                        <button
                          type="submit"
                          className={`w-full rounded-lg font-semibold py-3 disabled:opacity-60 ${isPreview ? 'bg-slate-100 text-slate-400' : 'text-white'}`}
                          style={isPreview ? undefined : { backgroundColor: 'var(--landing-accent, #1e3a8a)' }}
                          disabled={submitting || isPreview}
                        >
                          {isPreview ? '미리보기 모드(제출 불가)' : (submitting ? '접수 중…' : '상담 신청 접수')}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

        {landingSections
          .filter((section) => section.type !== 'hero')
          .map((section) => renderPersonalLandingBodySection(section, bodySectionCtx))}
      </main>
      <PersonalLandingOrgFooter name={name} />
    </div>
  )
}

